// プレイヤーの音を主役にする (2026-09-29 ユーザー「実際プレイするときにはプレイヤー主体で発生する音を優先して欲しい」)。
//   AI の言い直し「ボスの薙ぎを弾いた瞬間は、弾きの音が主役で薙ぎの風切りは脇へ」→ ユーザー「その理解であってます」。
//   仕組み = ボスの技の音だけを1本のつまみ (audio.ts Engine.bossDuck) に通し、プレイヤーの音が鳴った瞬間にそこを一時的に下げる。
//   分け方の正本はこのファイル。音の中身・音量の既定値は変えない (重なった時だけ引く)。
import type { SfxId } from './types'

// プレイヤーが起こした音 = 鳴ったらボスの技の音を下げる。
//   弾き・ガード・自分の振り・命中 (ボスが受けた音を含む)・被弾・回避・見切り・崩し・致命。
const PLAYER_SFX: ReadonlySet<SfxId> = new Set<SfxId>([
  'parry', 'guard_player', 'attack_swing', 'attack_swing_heavy',
  'hit_boss', 'hit_boss_heavy', 'hit_boss_blade_hide_shallow', 'hit_boss_blade_hide_deep', 'guard',
  'hit_player', 'dodge', 'perfect_dodge', 'counter_hit',
  'stagger', 'execute', 'execute_soul', 'execute_hit1', 'execute_hit2', 'execute_hit3', 'finisher_pull', 'finisher_name',
  'luna_alice_cuts', 'luna_alice_burst', 'luna_alice_cuts3', 'luna_alice_burst3', 'luna_exec_weight',
])

// 下げない音: 危険の赤い合図 (danger)・致命の合図 (fatal_ready)・武器が折れる/拾う (exec_weapon_*)・メニュー音 (ui_*)・
//   予告の音 (共通の唸り telegraph_*・騎士の予告 knight_tell_*・介錯の予告 exec_sfx_*_tell_*)。
//   = 身を守る/取りに行くための合図なので、重なっても引かない。
//   予告は 10-29 ユーザー「他の音と重なって予告音が消えていることがあります」から外した。介錯戦を2分回すと、攻め続ける時は
//   予告の 68%・何もしない時も 37% (被弾で下がる) が −9dB 下がった中で鳴っていた (記録 docs/research/2026-10-29-executioner-sfx.md)。
// ボスの技の音 = 振り・足音・花嫁や騎士の補助の合図・獣の声。
export function isPlayerSfx(id: SfxId): boolean {
  return PLAYER_SFX.has(id)
}
/** 予告の音 (下げない)。 */
export function isTellSfx(id: SfxId): boolean {
  const s = id as string
  return s.startsWith('telegraph_') || s.startsWith('knight_tell_') || (s.startsWith('exec_sfx_') && s.includes('_tell_'))
    || ((s.startsWith('bsfx_v1_') || s.startsWith('blade_sfx_v2_')) && s.includes('_tell_'))
}
/** 弾かれた時のボスの音 (bsfx_v1_*_parried*・blade_sfx_v2_parried)。弾きと同じ瞬間に鳴る相方の音なので下げない。 */
export function isBossParriedSfx(id: SfxId): boolean {
  const s = id as string
  return (s.startsWith('bsfx_v1_') || s.startsWith('blade_sfx_v2_')) && s.includes('_parried')
}
export function isBossMoveSfx(id: SfxId): boolean {
  const s = id as string
  if (isTellSfx(id) || isBossParriedSfx(id)) return false
  return s.startsWith('knight_sweep') || s.startsWith('exec_sfx_') || s.startsWith('bsfx_v1_') || s.startsWith('blade_sfx_v2_')
    || s.startsWith('knight_exec_') || s.startsWith('bride_parried_') || s.startsWith('hound_voice_') || s.startsWith('awoken_voice_')
    || s === 'knight_combo_tension' || s === 'bride_veil_snap' || s === 'bride_triple_chime' || s === 'rooster_rush_tick' || s === 'rooster_frenzy_cluck' || s === 'rooster_crow_short' || s === 'rooster_crow_long' || s === 'step'
}

// 下げ方。弾き (約0.4秒で消える) の芯が聞こえる間だけ引き、薙ぎの尾が戻ってくる形。
//   DEV `?duck=off` で下げない (聞き比べ用)・`?duck=<dB>` で深さを変える (例 ?duck=-6)。
export const BOSS_DUCK = {
  depthDb: -9,      // 下げる深さ (約 0.35 倍)
  attackSec: 0.006, // 下がりきるまで
  holdSec: 0.18,    // 下げたまま
  releaseSec: 0.35, // 戻るまで
}
let duckDepthDb: number | null = BOSS_DUCK.depthDb
export function getBossDuckDepthDb(): number | null { return duckDepthDb }
/** null = 下げない (?duck=off)。 */
export function setBossDuckDepthDb(db: number | null): void { duckDepthDb = db }

// ボスの当たりの音 (bsfx_v1_) は、当たった瞬間の束 (被弾・ガード) と同じ瞬間に鳴る。下げた後に鳴らすと出だしから 0.35 倍で、
//   被弾の音に埋もれる (11-01 作者「音が出てないときがある気がする」・鶏の一戦の再現で当たりの音の 7〜9 割が下がっていた
//   = docs/research/2026-11-01-rooster-sfx-ship.md)。同じ束の当たりの音は下げずに鳴らす。弾きが入った束だけは今まで通り弾きを主役にする。
export function keepsBossHitUp(ids: readonly SfxId[], id: SfxId): boolean {
  const s = id as string
  return (s.startsWith('bsfx_v1_') || s.startsWith('blade_sfx_v2_')) && isBossMoveSfx(id) && ids.some(isPlayerSfx) && !ids.includes('parry')
}

/** 同じ瞬間に鳴らす音の束のうち、プレイヤーの音があるか (あれば束を鳴らす前に下げる = ボスの音の出だしから引く)。 */
export function shouldDuckFor(ids: readonly SfxId[]): boolean {
  return duckDepthDb !== null && ids.some(isPlayerSfx)
}
